import React, { useMemo, useState } from 'react';

import { Button, Checkbox, Container, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import {
  OFFER_FACING_NAMES,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_LAST_MINUTE_HOTEL,
  OFFER_TYPE_TACTICAL_AO_HOTEL,
  OFFER_TYPE_TOUR,
} from '~/consts/offerTypes';

import { formatDateSlashes } from '~/services/TimeService';

import QuickSearchToolbar from '../../Common/Elements/QuickSearchToolbar';

interface Props {
  templates: Array<App.OfferTemplate>;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onApprove: (id: number) => void;
}

const FINE_PRINT_FIELD_RULES = [
  { label: 'Cancellation Policy', patterns: ['cancellation', 'cancel policy'] },
  { label: 'Date Change', patterns: ['date change', 'date changes'] },
  { label: 'Inclusion Notes', patterns: ['inclusion', 'inclusions'] },
  { label: 'Remaining Fine Print', patterns: ['remaining fine print'] },
];

function getFinePrintFieldLabels(template: App.OfferTemplate): string {
  if (template.template_type !== 'fine_print') {
    return '-';
  }

  const searchText = `${template.title} ${template.description}`.toLowerCase();
  const matches = FINE_PRINT_FIELD_RULES.filter(({ patterns }) =>
    patterns.some((pattern) => searchText.includes(pattern)),
  ).map(({ label }) => label);

  return matches.length > 0 ? matches.join(', ') : 'General';
}

const finePrintFieldColumn: GridColDef = {
  field: 'fine_print_field',
  headerName: 'Fine Print Field',
  sortable: false,
  disableColumnMenu: true,
  flex: 2,
  valueGetter: (_, row: App.OfferTemplate) => getFinePrintFieldLabels(row),
  display: 'flex',
};

export default function TemplateEditDeleteContainer({ templates: allTemplates, onApprove, onDelete, onEdit }: Props) {
  const [page, setPage] = useState(0);

  // setting to an empty object could simplify
  // filterTemplates(), but would complicate filter(e)
  const [filters, setFilters] = useState({
    offer_type: '',
    title: '',
    template_type: '',
  });

  const showFinePrintFieldColumn = filters.template_type === 'fine_print' && filters.offer_type === OFFER_TYPE_HOTEL;
  const templates = useMemo<Array<App.OfferTemplate>>(() => {
    return allTemplates.filter((template) => {
      for (const key in filters) {
        const filterKey = key as keyof typeof filters;
        const filterValue = filters[filterKey].toLowerCase();
        const templateValue = String(template[filterKey] ?? '').toLowerCase();
        const strictMatch = filterKey !== 'title';

        if (!checkPassFilter(filterValue, templateValue, strictMatch)) {
          return false;
        }
      }

      return true;
    });
  }, [allTemplates, filters]);

  const columns: Array<GridColDef> = [
    {
      field: 'title',
      headerName: 'Title',
      sortable: false,
      disableColumnMenu: true,
      minWidth: 100,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'template_type',
      headerName: 'Type',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      display: 'flex',
    },
    {
      field: 'description',
      headerName: 'Description',
      sortable: false,
      disableColumnMenu: true,
      flex: 3,
      display: 'flex',
    },
    ...(showFinePrintFieldColumn ? [finePrintFieldColumn] : []),
    {
      field: 'offer_type',
      headerName: 'Offer Type',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      valueFormatter(value) {
        if (value === 'all') {
          return value;
        }
        return OFFER_FACING_NAMES[value];
      },
      display: 'flex',
    },
    {
      field: 'updated_at',
      headerName: 'Date Modified',
      sortable: false,
      disableColumnMenu: true,
      flex: 0,
      minWidth: 130,
      valueFormatter(value) {
        return formatDateSlashes(value);
      },
      display: 'flex',
    },
    {
      field: 'templateActions',
      headerName: 'Actions',
      sortable: false,
      disableColumnMenu: true,
      // width: 125,
      flex: 1,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Stack direction={{ sm: 'column', md: 'row' }} spacing={2}>
            <Button onClick={() => onEdit(params.row.id)}>Edit</Button>
            <Button onClick={() => onDelete(params.row.id)} color="error">
              Delete
            </Button>
          </Stack>
        );
      },
      display: 'flex',
    },
    {
      field: 'templateApprove',
      headerName: 'Approved',
      sortable: false,
      disableColumnMenu: true,
      // width: 100,
      flex: 1,
      align: 'center',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Checkbox
            checked={params.row.status === 'approved'}
            onChange={() => onApprove(params.row.id)}
            sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
          />
        );
      },
      display: 'flex',
    },
  ];

  /**Sets filters, which triggers filtration */
  function filter(e) {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  }

  function checkPassFilter(rule: string, test: string, strictMatch: boolean): boolean {
    // if there is no rule, then it passes
    if (rule === '') {
      return true;
    }

    if (strictMatch) {
      return test === rule;
    }

    // partial match
    return test.includes(rule);
  }

  return (
    <Container maxWidth="xl">
      <FormControl sx={{ mt: 2 }} fullWidth>
        <InputLabel id="template-type-label">Template Type</InputLabel>
        <Select
          id="template_type"
          labelId="template-type-label"
          label="Template Type"
          name="template_type"
          onChange={filter}
          value={filters.template_type}
        >
          <MenuItem value=""></MenuItem>
          <MenuItem value="location">Location</MenuItem>
          <MenuItem value="fine_print">Fine print</MenuItem>
        </Select>
      </FormControl>
      <FormControl sx={{ mt: 2 }} fullWidth>
        <InputLabel id="offer-type-label">Offer Type</InputLabel>
        <Select
          id="offer_type"
          labelId="offe-type-label"
          label="Offer Type"
          name="offer_type"
          onChange={filter}
          value={filters.offer_type}
        >
          <MenuItem value=""></MenuItem>
          <MenuItem value={OFFER_TYPE_HOTEL}>{OFFER_FACING_NAMES[OFFER_TYPE_HOTEL]}</MenuItem>
          <MenuItem value={OFFER_TYPE_TOUR}>{OFFER_FACING_NAMES[OFFER_TYPE_TOUR]}</MenuItem>
          <MenuItem value={OFFER_TYPE_LAST_MINUTE_HOTEL}>{OFFER_FACING_NAMES[OFFER_TYPE_LAST_MINUTE_HOTEL]}</MenuItem>
          <MenuItem value={OFFER_TYPE_TACTICAL_AO_HOTEL}>{OFFER_FACING_NAMES[OFFER_TYPE_TACTICAL_AO_HOTEL]}</MenuItem>
          <MenuItem value="all">All offer types</MenuItem>
        </Select>
      </FormControl>
      <DataGrid
        className="T-offer-templates-table"
        rows={templates}
        columns={columns}
        pageSizeOptions={[10]}
        paginationModel={{ pageSize: 10, page }}
        onPaginationModelChange={({ page }) => setPage(page)}
        getRowId={(row: App.OfferTemplate) => row.id}
        rowCount={templates.length}
        getRowHeight={() => 'auto'}
        slots={{
          pagination: GridPagination,
          toolbar: QuickSearchToolbar,
        }}
        autoHeight // dangerous if number of rows per page > 25
        sx={{ mt: 2 }}
      />
    </Container>
  );
}
